import { supabase } from "@/integrations/supabase/client";

export interface SecurityEvent {
  event_type: string;
  event_details?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
}

/**
 * Security audit service for logging security events
 */
export class SecurityAuditService {
  /**
   * Log a security event via edge function
   */
  static async logSecurityEvent(event: SecurityEvent): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.warn('No active session for security logging');
        return;
      }

      await fetch(
        'https://akdhsybpxfexhkzsteih.supabase.co/functions/v1/log-security-event',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event_type: event.event_type,
            event_details: event.event_details,
            ip_address: event.ip_address,
            user_agent: event.user_agent || navigator.userAgent
          })
        }
      );
    } catch (error) {
      console.error('Error logging security event:', error);
    }
  }

  /**
   * Log successful login
   */
  static async logLoginSuccess(additionalData?: Record<string, any>): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'login_success',
      event_details: additionalData
    });
  }

  /**
   * Log failed login attempt
   */
  static async logLoginFailure(reason?: string, additionalData?: Record<string, any>): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'login_failed',
      event_details: { reason, ...additionalData }
    });
  }

  /**
   * Log password change
   */
  static async logPasswordChange(): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'password_change'
    });
  }

  /**
   * Log 2FA enabled
   */
  static async log2FAEnabled(): Promise<void> {
    await this.logSecurityEvent({
      event_type: '2fa_enabled'
    });
  }

  /**
   * Log 2FA disabled
   */
  static async log2FADisabled(): Promise<void> {
    await this.logSecurityEvent({
      event_type: '2fa_disabled'
    });
  }

  /**
   * Log 2FA verification attempt
   */
  static async log2FAVerification(success: boolean, attemptType: 'verify' | 'recovery'): Promise<void> {
    await this.logSecurityEvent({
      event_type: '2fa_verification_attempt',
      event_details: { success, attempt_type: attemptType }
    });
  }

  /**
   * Log property view (for buyer activity)
   */
  static async logPropertyView(propertyId: string, propertyAddress?: string): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'property_view',
      event_details: {
        property_id: propertyId,
        property_address: propertyAddress
      }
    });
  }

  /**
   * Log signup
   */
  static async logSignup(userType?: string): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'signup',
      event_details: { user_type: userType }
    });
  }

  /**
   * Log 2FA recovery codes regenerated
   */
  static async log2FARecoveryCodesRegenerated(): Promise<void> {
    await this.logSecurityEvent({
      event_type: '2fa_recovery_codes_regenerated',
      event_details: {
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log buyer contact information access
   */
  static async logBuyerContactAccess(
    buyerId: string, 
    buyerName: string,
    granted: boolean,
    accessedVia: string,
    reason?: string
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: granted ? 'buyer_contact_info_viewed' : 'buyer_contact_access_denied',
      event_details: {
        buyer_id: buyerId,
        buyer_name: buyerName,
        access_granted: granted,
        accessed_via: accessedVia,
        reason: reason,
        contact_fields_accessed: granted ? ['email', 'phone_number'] : [],
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log email change request
   */
  static async logEmailChange(newEmail: string): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'email_change_requested',
      event_details: {
        new_email: newEmail.substring(0, 3) + '***', // Partial for privacy
        timestamp: new Date().toISOString(),
      }
    });
  }

  /**
   * Log admin status change
   */
  static async logAdminStatusChange(
    targetUserEmail: string,
    oldStatus: string,
    newStatus: string,
    reason: string
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'admin_status_change',
      event_details: {
        target_user: targetUserEmail,
        old_status: oldStatus,
        new_status: newStatus,
        reason: reason,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log admin accessing the leads list (PII bulk access)
   */
  static async logAdminLeadsListAccess(
    searchQuery?: string,
    filters?: { status?: string; interested?: string },
    resultCount?: number
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'admin_leads_list_access',
      event_details: {
        search_query: searchQuery || null,
        filters,
        result_count: resultCount,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log admin viewing a specific lead's PII
   */
  static async logAdminLeadDetailView(
    leadId: string,
    leadEmail: string,
    leadName: string
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'admin_lead_detail_view',
      event_details: {
        lead_id: leadId,
        lead_email: leadEmail.substring(0, 3) + '***',
        lead_name: leadName,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log admin updating a lead
   */
  static async logAdminLeadUpdate(
    leadId: string,
    leadEmail: string,
    changes: { oldStatus?: string; newStatus?: string; notesChanged: boolean }
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'admin_lead_update',
      event_details: {
        lead_id: leadId,
        lead_email: leadEmail.substring(0, 3) + '***',
        old_status: changes.oldStatus,
        new_status: changes.newStatus,
        notes_changed: changes.notesChanged,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log admin deleting a lead
   */
  static async logAdminLeadDelete(
    leadId: string,
    leadEmail: string,
    leadName: string
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'admin_lead_delete',
      event_details: {
        lead_id: leadId,
        lead_email: leadEmail.substring(0, 3) + '***',
        lead_name: leadName,
        timestamp: new Date().toISOString()
      }
    });
  }
}

export default SecurityAuditService;